import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Edit2, Trash2, Shield, Save, X, Search, RefreshCw,
  Award, Check, ChevronRight
} from 'lucide-react';
import api from '../../services/apiService';
import { toast } from 'react-toastify';
import OEMAccessDenied from './OEMAccessDenied';

const MetricCard = ({ title, value, icon, color = 'slate' }) => {
  const colors = {
    slate: 'bg-slate-100 text-slate-600',
    orange: 'bg-orange-100 text-orange-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    blue: 'bg-blue-100 text-blue-600',
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all hover:border-orange-300 group">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-2xl font-black text-slate-900 group-hover:text-orange-500 transition-colors">{value}</p>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{title}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colors[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

const OEMAuthorizationLevels = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [levels, setLevels] = useState([]);
  const [qualificationTypes, setQualificationTypes] = useState([]);
  const [qualificationCategories, setQualificationCategories] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    order: 0,
    color: '#F97316',
    required_documents: [],
    required_doc_groups: [],
  });

  // Document groups for OR logic
  const [newDocGroup, setNewDocGroup] = useState([]);
  const [requiredDocSearch, setRequiredDocSearch] = useState('');
  const [alternativeDocSearch, setAlternativeDocSearch] = useState('');

  // Check user permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const response = await api.get('/oem/dashboard/team/');
        const roles = response.data.my_roles || [];
        const hasMasterRole = roles.some(r => r.role === 'master');
        const hasOnlyRestrictedRoles = roles.length > 0 && !hasMasterRole;
        if (hasOnlyRestrictedRoles) {
          setAccessDenied(true);
        }
      } catch (err) {
        // If endpoint fails, user has no team roles - allow access
      }
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    if (!accessDenied) {
      loadData();
    }
  }, [accessDenied]);

  // Auto-select first level when levels are loaded
  useEffect(() => {
    if (levels.length > 0 && !selectedLevel) {
      setSelectedLevel(levels[0]);
    }
  }, [levels]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [levelsRes, qualTypesRes, categoriesRes] = await Promise.all([
        api.get('/oem/authorization-levels/'),
        api.get('/qualificationtype/'),
        api.get('/qualificationcategory/'),
      ]);
      setLevels(levelsRes.data);
      setQualificationTypes(qualTypesRes.data);
      setQualificationCategories(categoriesRes.data);
      if (selectedLevel) {
        const updated = levelsRes.data.find(l => l.id === selectedLevel.id);
        if (updated) setSelectedLevel(updated);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingLevel(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      order: levels.length,
      color: '#F97316',
      required_documents: [],
      required_doc_groups: [],
    });
    setNewDocGroup([]);
    setRequiredDocSearch('');
    setAlternativeDocSearch('');
    setShowModal(true);
  };

  const handleEdit = (level) => {
    setEditingLevel(level);
    let reqDocs = level.required_documents || [];
    if ((!reqDocs || reqDocs.length === 0) && level.required_documents_data?.length > 0) {
      reqDocs = level.required_documents_data.map(doc => doc.id);
    }
    setFormData({
      name: level.name || '',
      code: level.code || '',
      description: level.description || '',
      order: level.order || 0,
      color: level.color || '#F97316',
      required_documents: reqDocs,
      required_doc_groups: level.required_doc_groups || [],
    });
    setNewDocGroup([]);
    setRequiredDocSearch('');
    setAlternativeDocSearch('');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this authorization level?')) return;
    try {
      await api.delete(`/oem/authorization-levels/${id}/`);
      toast.success('Authorization level deleted');
      if (selectedLevel?.id === id) {
        setSelectedLevel(null);
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      toast.error('Please fill in name and code');
      return;
    }

    console.log('=== SUBMIT DEBUG ===');
    console.log('Full formData:', formData);
    console.log('required_doc_groups:', formData.required_doc_groups);
    console.log('===================');

    setSaving(true);
    try {
      if (editingLevel) {
        const response = await api.put(`/oem/authorization-levels/${editingLevel.id}/`, formData);
        console.log('API Response:', response.data);
        toast.success('Authorization level updated');
      } else {
        const response = await api.post('/oem/authorization-levels/', formData);
        console.log('API Response:', response.data);
        toast.success('Authorization level created');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('API Error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDocument = (docId) => {
    setFormData(prev => ({
      ...prev,
      required_documents: prev.required_documents.includes(docId)
        ? prev.required_documents.filter(id => id !== docId)
        : [...prev.required_documents, docId]
    }));
  };

  const handleAddDocGroup = () => {
    console.log('=== ADD GROUP DEBUG ===');
    console.log('newDocGroup:', newDocGroup);
    console.log('Current required_doc_groups:', formData.required_doc_groups);

    if (newDocGroup.length < 1) {
      toast.error('Select at least 1 document for alternative group');
      return;
    }
    const updatedGroups = [...formData.required_doc_groups, newDocGroup];
    console.log('Updated groups will be:', updatedGroups);

    setFormData(prev => ({
      ...prev,
      required_doc_groups: [...prev.required_doc_groups, newDocGroup]
    }));
    setNewDocGroup([]);
    toast.success(`Group with ${newDocGroup.length} certificate(s) added`);
  };

  const handleRemoveDocGroup = (index) => {
    setFormData(prev => ({
      ...prev,
      required_doc_groups: prev.required_doc_groups.filter((_, i) => i !== index)
    }));
  };

  const handleToggleDocInGroup = (docId) => {
    setNewDocGroup(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const getDocumentName = (docId) => {
    const doc = qualificationTypes.find(q => q.id === docId);
    return doc ? doc.name : `Document ${docId}`;
  };

  const getCategoryName = (categoryId) => {
    const category = qualificationCategories.find(c => c.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  const groupQualificationsByCategory = (qualifications) => {
    const grouped = {};
    qualifications.forEach(qual => {
      const categoryId = qual.category || 'uncategorized';
      const categoryName = qual.category ? getCategoryName(qual.category) : 'Uncategorized';
      if (!grouped[categoryId]) {
        grouped[categoryId] = {
          id: categoryId,
          name: categoryName,
          qualifications: []
        };
      }
      grouped[categoryId].qualifications.push(qual);
    });
    // Sort by category name, with Uncategorized at the end
    return Object.values(grouped).sort((a, b) => {
      if (a.id === 'uncategorized') return 1;
      if (b.id === 'uncategorized') return -1;
      return a.name.localeCompare(b.name);
    });
  };

  const colorOptions = [
    { value: '#F97316', label: 'Orange' },
    { value: '#3B82F6', label: 'Blue' },
    { value: '#10B981', label: 'Green' },
    { value: '#F59E0B', label: 'Amber' },
    { value: '#EF4444', label: 'Red' },
    { value: '#8B5CF6', label: 'Purple' },
    { value: '#EC4899', label: 'Pink' },
    { value: '#14B8A6', label: 'Teal' },
  ];

  const filteredRequiredDocs = qualificationTypes.filter(qual =>
    !requiredDocSearch || qual.name.toLowerCase().includes(requiredDocSearch.toLowerCase())
  );

  const filteredAlternativeDocs = qualificationTypes.filter(qual =>
    !alternativeDocSearch || qual.name.toLowerCase().includes(alternativeDocSearch.toLowerCase())
  );

  const sortedLevels = [...levels].sort((a, b) => a.order - b.order);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-slate-600 font-medium">Loading authorization levels...</span>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return <OEMAccessDenied message="You don't have permission to manage Authorization Levels." />;
  }

  return (
    <div className="text-slate-900">
      <div className="p-8 max-w-7xl mx-auto">
        {/* Action Buttons */}
        <div className="flex justify-end items-center gap-3 mb-6">
          <button
            onClick={loadData}
            className="p-2.5 bg-white rounded-xl border border-slate-200 text-slate-500 hover:text-orange-500 hover:border-orange-300 transition-all shadow-sm"
            title="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={handleCreate}
            className="h-10 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 font-black text-[11px] uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-orange-500/20"
          >
            <Plus className="h-4 w-4" />
            Add Level
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <MetricCard title="Total Levels" value={levels.length} icon={<Shield size={18} />} />
          <MetricCard title="Certificates" value={qualificationTypes.length} icon={<Award size={18} />} color="orange" />
          <MetricCard
            title="With Requirements"
            value={levels.filter(l => l.required_documents_data?.length > 0).length}
            icon={<Check size={18} />}
            color="emerald"
          />
          <MetricCard
            title="With Alternatives"
            value={levels.filter(l => l.required_doc_groups?.length > 0).length}
            icon={<Shield size={18} />}
            color="blue"
          />
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left - Levels List */}
          <div className="lg:col-span-1 space-y-4">
            <h3 className="text-[10px] font-black tracking-[0.2em] uppercase text-orange-500 px-1">
              Authorization Tiers
            </h3>
            <div className="space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto pr-2">
              {sortedLevels.length === 0 ? (
                <div className="p-8 text-center bg-white rounded-2xl border border-slate-200">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Shield size={20} className="text-slate-400" />
                  </div>
                  <p className="text-slate-500 text-xs font-medium">No levels defined</p>
                  <button
                    onClick={handleCreate}
                    className="mt-3 text-orange-500 text-xs font-bold hover:underline"
                  >
                    Create first level →
                  </button>
                </div>
              ) : (
                sortedLevels.map((level, index) => (
                  <button
                    key={level.id}
                    onClick={() => setSelectedLevel(level)}
                    className={`w-full p-3 rounded-xl border transition-all text-left flex items-center gap-3 ${
                      selectedLevel?.id === level.id
                        ? 'bg-orange-500 border-orange-400 text-white shadow-lg shadow-orange-500/20'
                        : 'bg-white border-slate-200 hover:border-orange-300 text-slate-900 shadow-sm'
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs ${
                        selectedLevel?.id === level.id
                          ? 'bg-white/20 text-white'
                          : 'bg-orange-100 text-orange-600'
                      }`}
                    >
                      {level.code}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="block font-bold text-xs leading-tight truncate">
                        {level.name}
                      </span>
                      <span className={`block text-[9px] font-medium mt-0.5 ${
                        selectedLevel?.id === level.id ? 'text-white/70' : 'text-slate-400'
                      }`}>
                        {level.required_documents_data?.length || 0} certs
                      </span>
                    </div>
                    <ChevronRight size={12} className={selectedLevel?.id === level.id ? 'text-white/70' : 'text-slate-300'} />
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right - Level Details */}
          <div className="lg:col-span-2">
            {selectedLevel ? (
              <div className="bg-white rounded-3xl border border-slate-200 p-8 relative overflow-hidden shadow-sm">
                <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/5 blur-[100px] rounded-full"></div>

                <div className="flex items-start gap-6 relative z-10 mb-8">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-black text-white shadow-lg"
                    style={{ backgroundColor: selectedLevel.color || '#F97316' }}
                  >
                    {selectedLevel.code}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                          {selectedLevel.name}
                        </h2>
                        <p className="text-orange-500 font-black text-[10px] tracking-[0.2em] uppercase mt-1">
                          Authorization Tier • Order {selectedLevel.order}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(selectedLevel)}
                          className="p-2 bg-slate-100 rounded-xl border border-slate-200 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-all"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(selectedLevel.id)}
                          className="p-2 bg-slate-100 rounded-xl border border-slate-200 hover:bg-red-500 hover:text-white hover:border-red-500 transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    {selectedLevel.description && (
                      <p className="text-slate-500 text-sm mt-2">{selectedLevel.description}</p>
                    )}
                  </div>
                </div>

                {/* Mandatory Certificates */}
                <div className="relative z-10 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xs font-black flex items-center gap-3 text-slate-900 uppercase tracking-tight">
                      <Award size={14} className="text-orange-500" />
                      Mandatory Certificates
                    </h4>
                    <div className="h-px flex-1 bg-slate-200 mx-4"></div>
                  </div>

                  {selectedLevel.required_documents_data?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedLevel.required_documents_data.map((doc) => (
                        <div
                          key={doc.id}
                          className="p-4 bg-slate-50 rounded-2xl border border-slate-200 flex items-center gap-3 group hover:border-orange-300 transition-all"
                        >
                          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                            <Award size={14} className="text-orange-600" />
                          </div>
                          <div className="flex-1">
                            <span className="font-bold text-sm text-slate-900">{doc.name}</span>
                          </div>
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-[9px] font-black uppercase">
                            Required
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200 text-center">
                      <Award size={24} className="text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 text-xs">No mandatory certificates</p>
                      <button
                        onClick={() => handleEdit(selectedLevel)}
                        className="mt-2 text-orange-500 text-xs font-bold hover:underline"
                      >
                        Add certificates →
                      </button>
                    </div>
                  )}
                </div>

                {/* Alternative Groups */}
                {selectedLevel.required_doc_groups?.length > 0 && (
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-xs font-black flex items-center gap-3 text-slate-900 uppercase tracking-tight">
                        <Shield size={14} className="text-amber-500" />
                        Alternative Groups
                      </h4>
                      <div className="h-px flex-1 bg-slate-200 mx-4"></div>
                    </div>

                    <div className="space-y-3">
                      {selectedLevel.required_doc_groups.map((group, index) => (
                        <div key={index} className="p-4 bg-amber-50 rounded-2xl border border-amber-200">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded-lg text-[9px] font-black uppercase">
                              Group {index + 1}
                            </span>
                            <span className="text-[10px] text-amber-600 font-medium">One of these required</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {group.map((docId, docIndex) => (
                              <span
                                key={docIndex}
                                className="px-3 py-1.5 bg-white text-amber-800 rounded-lg text-xs font-bold border border-amber-200"
                              >
                                {getDocumentName(docId)}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-slate-200 p-12 text-center shadow-sm">
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Shield size={28} className="text-slate-300" />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-1">Select a Level</h3>
                <p className="text-slate-500 text-sm">Choose an authorization level to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-lg font-black text-slate-900">
                  {editingLevel ? 'Edit Authorization Level' : 'New Authorization Level'}
                </h3>
              </div>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>

            {/* Content - Three Columns */}
            <div className="flex-1 overflow-hidden flex">
              {/* Left Column - Basic Info */}
              <div className="w-[320px] p-5 border-r border-slate-100 overflow-y-auto space-y-4">
                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Basic Information</h4>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Level 1 - Basic Access"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Code *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., L1"
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    placeholder="Description..."
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm resize-none"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none text-sm"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Color</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {colorOptions.map(color => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                        className={`w-7 h-7 rounded-lg border-2 transition-all ${
                          formData.color === color.value ? 'border-slate-900 scale-110' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color.value }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Middle Column - Required Documents */}
              <div className="flex-1 border-r border-slate-100 flex flex-col">
                <div className="bg-orange-50 px-4 py-3 border-b border-orange-100">
                  <h4 className="text-[10px] font-black text-orange-900 uppercase tracking-widest">Required Certificates</h4>
                  <p className="text-[10px] text-orange-600">ALL selected must be valid</p>
                </div>
                <div className="p-2 border-b border-slate-100">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={requiredDocSearch}
                      onChange={(e) => setRequiredDocSearch(e.target.value)}
                      placeholder="Search certificates..."
                      className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none text-sm"
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                  {groupQualificationsByCategory(filteredRequiredDocs).map(category => (
                    <div key={category.id} className="mb-3">
                      <div className="sticky top-0 bg-white px-2 py-1.5 border-b border-slate-100 mb-1">
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                          {category.name}
                        </span>
                        <span className="ml-2 text-[9px] text-slate-400">
                          ({category.qualifications.filter(q => formData.required_documents.includes(q.id)).length}/{category.qualifications.length})
                        </span>
                      </div>
                      {category.qualifications.map(qual => (
                        <label
                          key={qual.id}
                          className={`flex items-center p-2 rounded-lg cursor-pointer transition-colors ${
                            formData.required_documents.includes(qual.id) ? 'bg-orange-100' : 'hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.required_documents.includes(qual.id)}
                            onChange={() => handleToggleDocument(qual.id)}
                            className="mr-2 accent-orange-500"
                          />
                          <span className="text-sm truncate">{qual.name}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-500 font-bold">
                  Selected: {formData.required_documents.length}
                </div>
              </div>

              {/* Right Column - Alternative Groups */}
              <div className="w-[320px] flex flex-col">
                <div className="bg-amber-50 px-4 py-3 border-b border-amber-100">
                  <div className="flex items-center gap-2">
                    <h4 className="text-[10px] font-black text-amber-900 uppercase tracking-widest">Alternative Groups</h4>
                    <span className="text-[8px] font-medium text-amber-500 bg-amber-100 px-1.5 py-0.5 rounded">OPTIONAL</span>
                  </div>
                  <p className="text-[10px] text-amber-600">ONE from each group must be valid (if set)</p>
                </div>
                {formData.required_doc_groups.length > 0 && (
                  <div className="p-2 border-b border-slate-100 max-h-24 overflow-y-auto space-y-1">
                    {formData.required_doc_groups.map((group, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg text-xs">
                        <span className="font-bold text-amber-700">OR {index + 1}:</span>
                        <span className="text-amber-600 truncate flex-1">{group.map(docId => getDocumentName(docId)).join(' / ')}</span>
                        <button onClick={() => handleRemoveDocGroup(index)} className="text-red-500 hover:text-red-700">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="p-2 border-b border-slate-100">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      value={alternativeDocSearch}
                      onChange={(e) => setAlternativeDocSearch(e.target.value)}
                      placeholder="Search certificates..."
                      className="w-full h-9 pl-9 pr-3 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none text-sm"
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                  {groupQualificationsByCategory(filteredAlternativeDocs).map(category => (
                    <div key={category.id} className="mb-3">
                      <div className="sticky top-0 bg-white px-2 py-1.5 border-b border-slate-100 mb-1">
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                          {category.name}
                        </span>
                        <span className="ml-2 text-[9px] text-slate-400">
                          ({category.qualifications.filter(q => newDocGroup.includes(q.id)).length}/{category.qualifications.length})
                        </span>
                      </div>
                      {category.qualifications.map(qual => (
                        <label
                          key={qual.id}
                          className={`flex items-center p-2 rounded-lg cursor-pointer transition-colors ${
                            newDocGroup.includes(qual.id) ? 'bg-amber-100' : 'hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={newDocGroup.includes(qual.id)}
                            onChange={() => handleToggleDocInGroup(qual.id)}
                            className="mr-2 accent-amber-500"
                          />
                          <span className="text-sm truncate">{qual.name}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="px-4 py-3 bg-amber-50 border-t border-amber-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10px] font-bold ${newDocGroup.length >= 1 ? 'text-amber-700' : 'text-slate-500'}`}>
                      Selected: {newDocGroup.length}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddDocGroup}
                    disabled={newDocGroup.length < 1}
                    className={`w-full px-3 py-2.5 text-xs font-black uppercase tracking-wide rounded-lg transition-all ${
                      newDocGroup.length >= 1
                        ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-lg shadow-amber-500/30 animate-pulse'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                  >
                    {newDocGroup.length >= 1 ? `+ Add Group (${newDocGroup.length} cert${newDocGroup.length > 1 ? 's' : ''})` : 'Select certificate(s)'}
                  </button>
                  {formData.required_doc_groups.length > 0 && (
                    <p className="text-[9px] text-amber-600 mt-2 text-center font-medium">
                      {formData.required_doc_groups.length} group(s) ready to save
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3 bg-slate-50 flex-shrink-0">
              <button onClick={() => setShowModal(false)} className="h-9 px-4 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-bold text-sm">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="h-9 px-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2 font-bold text-sm"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {editingLevel ? 'Update' : 'Create'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OEMAuthorizationLevels;
